/**
 * Simple encryption/decryption utility for obfuscating puzzle data
 * Uses Base64 encoding with a simple XOR cipher
 */

// Simple key for XOR encryption (can be changed)
const ENCRYPTION_KEY = 'LDDA_PUZZLE_KEY_2024';

/**
 * XOR cipher function
 * @param {string} text - Text to encrypt/decrypt
 * @param {string} key - Encryption key
 * @returns {string} - XOR result
 */
function xorCipher(text, key) {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    const textChar = text.charCodeAt(i);
    const keyChar = key.charCodeAt(i % key.length);
    result += String.fromCharCode(textChar ^ keyChar);
  }
  return result;
}

/**
 * Encrypt puzzle data
 * @param {Object} puzzleData - Puzzle object to encrypt
 * @returns {string} - Encrypted string
 */
export function encryptPuzzle(puzzleData) {
  try {
    // Convert to JSON string
    const jsonString = JSON.stringify(puzzleData);
    
    // Apply XOR cipher
    const xorResult = xorCipher(jsonString, ENCRYPTION_KEY);
    
    // Encode to Base64
    const encrypted = btoa(xorResult);
    
    return encrypted;
  } catch (error) {
    console.error('Error encrypting puzzle:', error);
    return null;
  }
}

/**
 * Decrypt puzzle data
 * @param {string} encryptedData - Encrypted string
 * @returns {Object|null} - Decrypted puzzle object or null if error
 */
export function decryptPuzzle(encryptedData) {
  let jsonString = '';
  try {
    // Decode from Base64
    const xorResult = atob(encryptedData);
    
    // Apply XOR cipher (XOR is symmetric)
    jsonString = xorCipher(xorResult, ENCRYPTION_KEY);
    
    // Parse JSON
    const puzzleData = JSON.parse(jsonString);
    
    return puzzleData;
  } catch (error) {
    console.error('Error decrypting puzzle:', error);
    return null;
  }
}

/**
 * Check if data appears to be encrypted
 * @param {any} data - Data to check
 * @returns {boolean} - True if appears encrypted
 */
export function isEncrypted(data) {
  if (typeof data !== 'string') return false;
  
  // Check if it looks like Base64
  const base64Regex = /^[A-Za-z0-9+/]+=*$/;
  return base64Regex.test(data) && data.length > 20;
}

/**
 * Encrypt all puzzles in a game object
 * @param {Object} game - Game object
 * @returns {Object} - Game object with encrypted puzzles
 */
export function encryptGamePuzzles(game) {
  if (!game || !game.scenes) return game;
  
  const gameClone = JSON.parse(JSON.stringify(game));
  
  gameClone.scenes = gameClone.scenes.map(scene => {
    if (scene.puzzle) {
      scene.puzzle = encryptPuzzle(scene.puzzle);
    }
    if (scene.puzzle_reduced) {
      scene.puzzle_reduced = encryptPuzzle(scene.puzzle_reduced);
    }
    return scene;
  });
  
  return gameClone;
}

/**
 * Decrypt all puzzles in a game object
 * @param {Object} game - Game object with encrypted puzzles
 * @returns {Object} - Game object with decrypted puzzles
 */
export function decryptGamePuzzles(game) {
  if (!game || !game.scenes) return game;
  
  const gameClone = JSON.parse(JSON.stringify(game));
  
  gameClone.scenes = gameClone.scenes.map(scene => {
    if (scene.puzzle && isEncrypted(scene.puzzle)) {
      scene.puzzle = decryptPuzzle(scene.puzzle);
    }
    if (scene.puzzle_reduced && isEncrypted(scene.puzzle_reduced)) {
      scene.puzzle_reduced = decryptPuzzle(scene.puzzle_reduced);
    }
    return scene;
  });
  
  return gameClone;
}
